package gr.tech.site.pages.landings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Background
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.css.functions.toImage
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.FaCheck
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.pages.SitePage
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initFinancingPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Financing for Commercial Growers — Equipment Upgrades Without the Wait",
        description = "Don't let upfront equipment costs delay your grow operation upgrades. Green Rebates connects commercial growers with financing options that work alongside your pending utility rebates.",
        canonicalUrl = "https://greenrebates.com/financing",
        ogImage = "/images/moneybackground.png"
    ))
}

@Page(SitePage.Landing.FINANCING)
@Layout(".components.layouts.PageLayout")
@Composable
fun FinancingPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        ) {
            Img(
                src = ImageRepo.MoneyBackground.src,
                alt = ImageRepo.MoneyBackground.alt,
                attrs = Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Cover)
                    .toAttrs { attr("fetchpriority", "high") }
            )
            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .background(
                        Background.of(
                            image = linearGradient(
                                from = Theme.DarkGreen.rgb,
                                to = rgba(2, 67, 66, 0),
                                dir = LinearGradient.Direction.ToRight
                            ).toImage()
                        )
                    )
                    .toAttrs()
            )
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                H3(attrs = LandingHeroEyebrowStyle.toModifier().classNames("text-uppercase", "mb-2").toAttrs()) {
                    Text("Rebate-Backed Financing for Equipment Upgrades")
                }
                H1(attrs = LandingHeroHeadlineStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Upgrade Now. Let the Rebate Help Pay for It.")
                }
                P(attrs = LandingHeroLeadStyle.toModifier().classNames("mb-4").toAttrs()) {
                    Text("Upfront equipment costs shouldn't hold your grow operation back. Green Rebates helps commercial growers access financing options that work alongside your pending utility rebates — so you can move forward without draining working capital.")
                }
                Link(
                    path = "/contact-us",
                    text = "Talk to Us About Financing \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaPrimaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Talk to Green Rebates about financing options for grow operation equipment") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // HOW REBATE-BACKED FINANCING WORKS — unique structure, replaces "types of rebates" grid
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-2").toAttrs()) {
                    Text("How Rebate-Backed Financing Works")
                }
                P(attrs = LandingSectionSubtitleStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Combine financing with your pending utility rebates to upgrade your facility now and let the incentive do the heavy lifting.")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4").toAttrs()) {
                    listOf(
                        Triple("1", "We Calculate Your Total Rebate Value",
                            "Before financing anything, we determine the total rebate amount your facility qualifies for — LED lighting, HVAC, dehumidification, or all three. You know your number before you commit to a dollar."),
                        Triple("2", "Financing Bridges the Upfront Gap",
                            "Equipment upgrades don't have to wait for a rebate check. Financing options let you install the equipment now while your rebate application is actively in progress."),
                        Triple("3", "Your Rebate Offsets the Cost",
                            "Once your utility rebate is approved and paid, it can directly offset your financing balance — reducing your net out-of-pocket cost significantly, often to near zero.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3").toAttrs()) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(attrs = LandingStepCardTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                P(attrs = LandingStepCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                            }
                        }
                    }
                }
            }
        }

        // BENEFITS — split layout with checklist
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center", "g-4").toAttrs()) {

                    // Left — copy + checklist + CTA
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Why Combine Financing With Your Rebates")
                            }
                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("Waiting to upgrade until you have cash on hand costs you every month in energy bills and missed production capacity. Financing lets commercial growers move immediately — and the rebate follows.")
                            }
                            Ul(
                                attrs = Modifier
                                    .classNames("list-unstyled", "d-flex", "flex-column", "gap-2", "text-start", "w-100", "mb-0")
                                    .toAttrs()
                            ) {
                                listOf(
                                    "No large upfront payment required to begin your equipment upgrade",
                                    "Your utility rebate can offset a significant share of your financing balance",
                                    "Preserve working capital for cultivation operations while upgrading equipment",
                                    "Energy savings start immediately — reducing operating costs from day one"
                                ).forEach { item ->
                                    Li(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                                        FaCheck(
                                            modifier = LandingCheckmarkStyle.toModifier().classNames("flex-shrink-0"),
                                            size = IconSize.SM
                                        )
                                        Span { Text(item) }
                                    }
                                }
                            }
                            Link(
                                path = "/contact-us",
                                text = "Talk to Us About Financing Options \u2192",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .classNames("mt-2")
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Talk to Green Rebates about financing options for your grow operation") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }
                    }

                    // Right — image
                    // TODO: Replace with a dedicated financing/equipment image
                    Div(attrs = Modifier.classNames("col-lg-6").toAttrs()) {
                        Img(
                            src = ImageRepo.CannabisPhoto.src,
                            alt = ImageRepo.CannabisPhoto.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingDarkSectionHeadingStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Why Get Financing Through Green Rebates")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4").toAttrs()) {
                    listOf(
                        "We Know the Rebate Side" to
                            "Unlike standalone lenders, Green Rebates understands the full rebate process. That means financing structures that actually align with when and how your utility rebate pays out.",
                        "One Relationship, Not Two" to
                            "You're already working with us on rebates. Adding financing support means no new vendors to manage, no duplicate paperwork, and a single point of contact for your entire upgrade.",
                        "Options for Every Facility Size" to
                            "Whether you're upgrading a single grow room or a large-scale cultivation facility, we connect you with financing that fits your operation's scale and timeline.",
                        "Grower-Specific Expertise" to
                            "We work exclusively with commercial growers, cannabis cultivators, and horticulture facilities — so our financing guidance is rooted in the realities of running a grow operation, not generic energy consulting."
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                                }
                            }
                        }
                    }
                }
            }
        }

        RelatedSection(listOf(
            "/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives" to "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives",
            "/article/why-you-should-use-a-rebate-company-for-your-horticulture-energy-rebates-and-incentives" to "Why You Should Use A Rebate Company for Your Horticulture Energy Rebates and Incentives",
            "/article/rebates-incentives-for-indoor-growers-what-you-need-to-know" to "Rebates vs. Incentives for Indoor Growers: What You Need to Know",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(attrs = LandingCtaHeadingStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Ready to Upgrade Without the Wait?")
                }
                P(attrs = LandingCtaBodyStyle.toModifier().classNames("mb-4", "mx-auto").toAttrs()) {
                    Text("Financing and rebates are better together. Green Rebates helps commercial growers move forward with LED, HVAC, and dehumidifier upgrades today — without a large upfront investment. No account required.")
                }
                Link(
                    path = "/contact-us",
                    text = "Contact Us to Explore Financing \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates to explore financing options for grow operation equipment upgrades") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}
